import subdomainConfigs from '../config/subdomainConfig';

export const getSubdomain = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  // Check if we're in development (localhost)
  if (hostname === 'localhost') {
    // Check URL parameters or pathname for subdomain in development
    const urlParams = new URLSearchParams(window.location.search);
    const subdomainParam = urlParams.get('subdomain');
    if (subdomainParam && subdomainConfigs[subdomainParam]) {
      return subdomainParam;
    }
    
    // Check pathname for subdomain (e.g., /mpizza/)
    const pathSegments = window.location.pathname.split('/').filter(segment => segment);
    if (pathSegments.length > 0 && subdomainConfigs[pathSegments[0]]) {
      return pathSegments[0];
    }
    
    // Default to 'juicy' for development if no specific subdomain detected
    return 'juicy';
  }
  
  // Check if we have a subdomain
  if (parts.length > 2) {
    const subdomain = parts[0];
    return subdomainConfigs[subdomain] ? subdomain : 'default';
  }
  
  return 'default';
};

export const getSubdomainConfig = () => {
  const subdomain = getSubdomain();
  return subdomainConfigs[subdomain] || subdomainConfigs.default;
};

// Helper function to get specific theme values
export const getThemeValue = (key) => {
  const config = getSubdomainConfig();
  return config.theme[key];
};

// Helper function to get specific branding values
export const getBrandingValue = (key) => {
  const config = getSubdomainConfig();
  return config.branding[key];
};

// Helper function to get company name for API calls
export const getCompanyName = () => {
  const subdomain = getSubdomain();
  
  // Special case: juicy should send "Juicy", everything else sends subdomain key
  if (subdomain === 'juicy') {
    return 'Juicy';
  }
  
  return subdomain;
};

// Helper function to get preset questions
export const getPresetQuestions = () => {
  const config = getSubdomainConfig();
  // Default fallback questions if none are configured
  const defaultQuestions = {
    suggested: [
      "Something refreshing packed with vitamin C 🍊",
      "A sweet and fruity beverage to satisfy my cravings 🍓",
      "An energizing drink to power through my day ⚡",
      "A healthy green juice for my wellness goals 🥬"
    ],
    flavors: [
      "Citrusy and zesty flavors 🍋",
      "Sweet berry combinations 🫐",
      "Tropical fruit medley 🥭",
      "Green apple and mint freshness 🍏"
    ],
    nutrition: [
      "High protein smoothie for post-workout 💪",
      "Low sugar option under 150 calories ⚖️",
      "Antioxidant-rich superfood blend 🌟",
      "Immune-boosting vitamin packed drink 🛡️"
    ],
    dietary: [
      "Vegan-friendly options 🌱",
      "Gluten-free beverages 🚫🌾",
      "Dairy-free smoothies 🥛❌",
      "Nut-free alternatives 🥜❌"
    ],
    adventurous: [
      "Something unique I've never tried before 🎲",
      "Spicy and bold flavor combinations 🌶️",
      "Vegetable-forward green juice challenge 🥒",
      "Exotic fruit I can't pronounce 🌺"
    ]
  };
  
  return config.presetQuestions || defaultQuestions;
};

// Helper function to get category-based fallback image
export const getCategoryFallbackImage = (category) => {
  const subdomain = getSubdomain();
  
  // Define fallback images based on subdomain and category
  const fallbackImages = {
    juicy: {
      drink: '/juiceIcon.png',
      smoothies: '/juiceIcon.png',
      healthy: '/juiceIcon.png',
      default: '/juiceIcon.png'
    },
    mpizza: {
      pizza: '/favicon.ico', // Temporary - will need pizza icon
      salads: '/juiceIcon.png', // Temporary - will need salad icon  
      desserts: '/iceCream.png', // Using existing ice cream icon
      shareables: '/favicon.ico', // Temporary - will need appetizers icon
      'subs&burgers': '/favicon.ico', // Temporary - will need sandwich icon
      default: '/favicon.ico'
    },
    littlemexico: {
      tacos: '/favicon.ico', // Temporary - will need taco icon
      burritos: '/favicon.ico', // Temporary - will need burrito icon
      enchiladas: '/favicon.ico', // Temporary - will need enchilada icon
      quesadillas: '/favicon.ico', // Temporary - will need quesadilla icon
      fajitas: '/favicon.ico', // Temporary - will need fajita icon
      margaritas: '/juiceIcon.png', // Using existing drink icon temporarily
      appetizers: '/favicon.ico', // Temporary - will need appetizer icon
      desserts: '/iceCream.png', // Using existing ice cream icon
      default: '/favicon.ico'
    },
    default: {
      appetizers: '/favicon.ico',
      entrees: '/favicon.ico', 
      drink: '/juiceIcon.png',
      desserts: '/iceCream.png',
      default: '/juiceIcon.png'
    }
  };
  
  const subdomainImages = fallbackImages[subdomain] || fallbackImages.default;
  return `${process.env.PUBLIC_URL}${subdomainImages[category] || subdomainImages.default}`;
};

// Helper function to get the correct image URL for any item
export const getItemImageUrl = (itemName, category, menuImages) => {
  if (!menuImages || !menuImages.images || !Array.isArray(menuImages.images)) {
    return getCategoryFallbackImage(category);
  }
  
  const imagesArray = menuImages.images;
  
  // Convert item name to match filename format (remove spaces, handle capitalization)
  const formatName = (name) => {
    return name.replace(/\s+/g, ''); // Remove all spaces
  };
  
  // Helper function to remove common image file extensions
  const removeImageExtension = (filename) => {
    // Handle various image extensions, including potential query parameters or extra characters
    return filename.replace(/\.(jpg|jpeg|png|avif|webp|gif|bmp|tiff|tif)(\?.*)?$/i, '');
  };
  
  const formattedItemName = formatName(itemName);
  
  // Debug logging (temporary)
  console.log('🔍 Looking for image for item:', itemName);
  console.log('📝 Formatted item name:', formattedItemName);
  console.log('📁 Available images:', imagesArray.map(img => img.filename));
  
  // Find matching image by comparing formatted names
  const matchingImage = imagesArray.find(img => {
    const imgNameWithoutExt = removeImageExtension(img.filename);
    console.log('🔄 Comparing:', imgNameWithoutExt.toLowerCase(), 'vs', formattedItemName.toLowerCase());
    return imgNameWithoutExt.toLowerCase() === formattedItemName.toLowerCase();
  });
  
  // If no exact match, try partial matching
  if (!matchingImage) {
    const partialMatch = imagesArray.find(img => {
      const imgNameWithoutExt = removeImageExtension(img.filename).toLowerCase();
      const itemNameLower = formattedItemName.toLowerCase();
      return imgNameWithoutExt.includes(itemNameLower) || itemNameLower.includes(imgNameWithoutExt);
    });
    
    if (partialMatch) {
      return partialMatch.url;
    }
  }
  
  return matchingImage ? matchingImage.url : getCategoryFallbackImage(category);
};

// Helper function to get featured products
export const getFeaturedProducts = () => {
  const config = getSubdomainConfig();
  // Default fallback products if none are configured
  const defaultFeaturedProducts = [
    {
      id: "featured_1",
      item_name: "Strawberry Quick",
      base_price: 6.99,
      description: "A sweet and refreshing blend of ripe strawberries, tropical pineapple, and a hint of spicy ginger for a burst of flavor and energy.",
      category: "drink",
      image: "StrawberryQuick.jpg",
      calories: 350,
      health_benefits: "Rich in antioxidants, supports digestion, and boosts immunity.",
      macros: { protein: 3, carbs: 84, fats: 5 },
      sizes: { regular: { price_diff: 0 }, large: { price_diff: 1.5 } },
      add_ons: [{ name: "Protein Scoop", price: 2 }]
    },
    {
      id: "featured_2", 
      item_name: "The Hulk",
      base_price: 6.99,
      description: "A revitalizing juice featuring a mix of pineapple, cucumber, green apple, and lemon, providing a refreshing and cooling effect.",
      category: "drink",
      image: "TheHulk.jpg",
      calories: 350,
      health_benefits: "Detoxifies the body and replenishes hydration with vital nutrients.",
      macros: { protein: 3, carbs: 83, fats: 5 },
      sizes: { regular: { price_diff: 0 }, large: { price_diff: 1.5 } },
      add_ons: [{ name: "Protein Scoop", price: 2 }]
    },
    {
      id: "featured_3",
      item_name: "Orange Juice",
      base_price: 6.99,
      description: "A classic, tangy, and refreshing juice made with organic oranges, offering a burst of vitamin C and citrus goodness.",
      category: "drink",
      image: "OrangeJuice.jpg",
      calories: 250,
      health_benefits: "Boosts immune system and provides hydration with natural electrolytes.",
      macros: { protein: 2, carbs: 60, fats: 1 },
      sizes: { regular: { price_diff: 0 }, large: { price_diff: 1.5 } },
      add_ons: [{ name: "Protein Scoop", price: 2 }]
    }
  ];
  
  return config.featuredProducts || defaultFeaturedProducts;
}; 